const CACHE_PREFIX = 'medtrainAutoTranslation:v6';
const memoryCache = new Map();

const stableHash = (value) => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const cacheKey = (language, text) => `${CACHE_PREFIX}:${language}:${stableHash(text)}`;

const readCache = (language, text) => {
  const key = cacheKey(language, text);
  if (memoryCache.has(key)) return memoryCache.get(key);
  try {
    const value = localStorage.getItem(key);
    if (value) {
      memoryCache.set(key, value);
      return value;
    }
  } catch {
    // Browser storage can be unavailable in private or test contexts.
  }
  return null;
};

const writeCache = (language, text, translated) => {
  if (!translated) return;
  const key = cacheKey(language, text);
  memoryCache.set(key, translated);
  try {
    localStorage.setItem(key, translated);
  } catch {
    // Best-effort cache only.
  }
};

const isTechnicalToken = (text) => {
  const trimmed = text.trim();
  if (!trimmed) return true;
  if (/^[\d\s.,:/%+<>=°()-]+$/.test(trimmed)) return true;
  if (/^[A-Z0-9/+\-_.]{1,10}$/.test(trimmed)) return true;
  if (/^https?:\/\//i.test(trimmed)) return true;
  return false;
};

export const hasMixedLanguageLeftovers = (text, language) => {
  if (!['ru', 'russian'].includes(String(language || '').toLowerCase())) return false;
  const allowed = new Set([
    'ECG', 'TSH', 'HBA1C', 'CKD', 'BP', 'HR', 'O2', 'DKA', 'NASH', 'T2DM', 'CGM',
    'DAFNE', 'IV', 'SGLT2', 'ACE', 'ESRD', 'NSTEMI', 'LVH', 'MR', 'SR', 'OD', 'PRN'
  ]);
  const words = String(text || '').match(/[A-Za-z][A-Za-z0-9+-]*/g) || [];
  return words.some((word) => !allowed.has(word.toUpperCase()));
};

export const humanizeKey = (key = '') => String(key).replace(/_/g, ' ');

export const collectTranslatableStrings = (value, options = {}) => {
  const includeKeys = Boolean(options.includeKeys);
  const excludedKeys = new Set(options.excludedKeys || ['id', 'case_id', 'case_number', 'created_by', 'image_filename', 'translations']);
  const seen = new Set();
  const items = [];

  const add = (text) => {
    const trimmed = String(text || '').trim();
    if (!trimmed || isTechnicalToken(trimmed) || seen.has(trimmed)) return;
    seen.add(trimmed);
    items.push(trimmed);
  };

  const walk = (node) => {
    if (node == null) return;
    if (typeof node === 'string') {
      add(node);
      return;
    }
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (typeof node === 'object') {
      Object.entries(node).forEach(([key, child]) => {
        if (excludedKeys.has(key)) return;
        if (includeKeys) add(humanizeKey(key));
        walk(child);
      });
    }
  };

  walk(value);
  return items;
};

export const translateStringsWithCache = async (texts, language, translateBatch, chunkSize = 80, options = {}) => {
  if (language === 'en') return Object.fromEntries(texts.map((text) => [text, text]));
  const retryMixed = options.retryMixed !== false;

  const map = {};
  const missing = [];
  texts.forEach((text) => {
    const cached = readCache(language, text);
    if (cached && !hasMixedLanguageLeftovers(cached, language)) {
      map[text] = cached;
    } else {
      missing.push(text);
    }
  });

  for (let index = 0; index < missing.length; index += chunkSize) {
    const chunk = missing.slice(index, index + chunkSize);
    const result = await translateBatch(chunk, language);
    const translated = Array.isArray(result?.data) ? [...result.data] : [];
    if (translated.length !== chunk.length) {
      throw new Error('translation count mismatch');
    }
    for (let offset = 0; retryMixed && offset < chunk.length; offset += 1) {
      if (!hasMixedLanguageLeftovers(translated[offset], language)) continue;
      try {
        const retry = await translateBatch([chunk[offset]], language);
        const retryText = Array.isArray(retry?.data) ? retry.data[0] : '';
        if (retryText && !hasMixedLanguageLeftovers(retryText, language)) {
          translated[offset] = retryText;
        }
      } catch {
        // Keep the original below; failed retries should not block the page.
      }
    }
    chunk.forEach((text, offset) => {
      const value = translated[offset] || text;
      if (hasMixedLanguageLeftovers(value, language)) {
        if (options.acceptMixed === false) return;
        map[text] = value;
        return;
      }
      map[text] = value;
      writeCache(language, text, value);
    });
  }

  return map;
};

export const translateStringsProgressively = async (
  texts,
  language,
  translateBatch,
  onUpdate,
  chunkSize = 10,
  options = {}
) => {
  if (language === 'en') {
    const englishMap = Object.fromEntries(texts.map((text) => [text, text]));
    onUpdate?.(englishMap);
    return englishMap;
  }

  const retryMixed = options.retryMixed !== false;
  const finalMap = {};
  const missing = [];

  texts.forEach((text) => {
    const cached = readCache(language, text);
    if (cached && !hasMixedLanguageLeftovers(cached, language)) {
      finalMap[text] = cached;
    } else {
      missing.push(text);
    }
  });

  if (Object.keys(finalMap).length) onUpdate?.({ ...finalMap });

  for (let index = 0; index < missing.length; index += chunkSize) {
    const chunk = missing.slice(index, index + chunkSize);
    try {
      const result = await translateBatch(chunk, language);
      const translated = Array.isArray(result?.data) ? [...result.data] : [];
      if (translated.length !== chunk.length) throw new Error('translation count mismatch');

      for (let offset = 0; retryMixed && offset < chunk.length; offset += 1) {
        if (!hasMixedLanguageLeftovers(translated[offset], language)) continue;
        try {
          const retry = await translateBatch([chunk[offset]], language);
          const retryText = Array.isArray(retry?.data) ? retry.data[0] : '';
          if (retryText && !hasMixedLanguageLeftovers(retryText, language)) {
            translated[offset] = retryText;
          }
        } catch {
          // Leave the item pending; the caller should keep showing a loading state.
        }
      }

      chunk.forEach((text, offset) => {
        const value = translated[offset];
        if (!value || hasMixedLanguageLeftovers(value, language)) return;
        finalMap[text] = value;
        writeCache(language, text, value);
      });

      onUpdate?.({ ...finalMap });
    } catch (error) {
      if (options.throwOnChunkError) throw error;
      onUpdate?.({ ...finalMap });
    }
  }

  return finalMap;
};

export const applyTranslationMap = (value, translations) => {
  if (typeof value === 'string') return translations[value.trim()] || value;
  if (Array.isArray(value)) return value.map((item) => applyTranslationMap(item, translations));
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, applyTranslationMap(child, translations)])
    );
  }
  return value;
};

export const translateDisplayKey = (key, translations) => {
  const label = humanizeKey(key);
  return translations[label] || label;
};
